import { CourseUtils } from './CourseUtils';
import { isRelativePath } from '../../scripts/generateTranslationBundles/utils/isRelativePath';
import { buildUri, CLOUDFRONT_ENDPOINT } from '../endpoints';
import { getCourseCurriculumLocaleData } from './getCourseCurriculumLocaleData';
import { DEFAULT_LOCALE_CODE } from '../const';

/**
 * A utility class useful for helping to determine things about a particular
 * curriculum. Use of this class is entirely optional.
 */
export class CurriculumUtils {
    
    /**
     * Retrieves the progress percentage from the provided curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @returns { number }
     */
    static getProgressPercentage( curriculumData ) {
        const courses = curriculumData?.courses;
        let finalPercentage = 0.0;

        if ( courses && courses.length > 0 ) {
            for ( let i = 0; i < courses.length; i++ ) {
                const object = courses[ i ];
                finalPercentage += CourseUtils.getProgressPercentage( object );
            }

            finalPercentage /= courses.length;
        }

        return finalPercentage;
    }

    /**
     * Retrieves the ID of the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getId( curriculumData ) {
        return curriculumData?.id;
    }

    /**
     * Returns the locale data for the curriculum. If there happens to be locale data stored
     * in the curriculum's metadata too, that will also be returned. If there are duplicate
     * locales between the official locales and the locales found in the metadata,
     * prioritize the official locale data.
     * 
     * @param { object } curriculumData - The data of the curriculum.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getLocales( curriculumData ) {
        return getCourseCurriculumLocaleData( curriculumData );
    }

    /**
     * Retrieves the name of the curriculum in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code
     * of `en-US` will be used.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getName( curriculumData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {
        // Same logic as courses.
        return CourseUtils.getName( curriculumData, { localeCode, allowFallback } );
    }

    /**
     * Retrieves the description of the curriculum in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code of
     * `en-US` will be used.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getDescription( curriculumData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {
        // Same logic as courses.
        return CourseUtils.getDescription( curriculumData, { localeCode, allowFallback } );
    }

    /**
     * Retrieves the list of tags assigned to the curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @param { boolean } onlyValid - Whether or not only valid tags will be retrieved.
     * @returns { { id: string, locales: { locale: string, name: string }[] }[] }
     */
    static getTags( curriculumData, onlyValid = true ) {
        // The Course Service currently doesn't support translations for the course/curriculum
        // tags. This means, for now, we can interpret all of these tags as the English version
        // of the tags.
        let tagData = curriculumData?.tags ? curriculumData.tags : [];

        if ( onlyValid ) {
            tagData = tagData.filter( ( tag ) => {
                return ( Boolean( tag ) && !isValidJsonString( tag ) );
            } );
        }

        return tagData.map( ( tag ) => ( {
            id: undefined,
            locales: [ { locale: 'en-US', name: tag } ],
        } ) );
    }

    /**
     * Retrieves the thumbnail image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getThumbnailImage( curriculumData ) {
        return curriculumData?.thumbnailImage;
    }

    /**
     * Retrieves the URL of the thumbnail image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getThumbnailImageUrl( curriculumData, { forceAbsolutePath = true } = {} ) {
        const path = this.getThumbnailImage( curriculumData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_ENDPOINT, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the hero image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getHeroImage( courseData ) {
        return courseData?.heroImage;
    }

    /**
     * Retrieves the URL of the hero image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getHeroImageUrl( curriculumData, { forceAbsolutePath = true } = {} ) {
        const path = this.getHeroImage( curriculumData );
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_ENDPOINT, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Returns the category IDs assigned to the provided curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @returns { string[] }
     */
    static getCategoryIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtils.getCategoryIds( curriculumData );
    }

    /**
     * Returns the brand IDs assigned to the provided curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @returns { string[] }
     */
    static getBrandIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtils.getBrandIds( curriculumData );
    }

    /**
     * Returns the company relationship IDs assigned to the curriculum
     * 
     * @param {object} curriculumData - The curriculum data.
     * @returns { string[] } company relationship UUIDs
     */
    static getCompanyRelationshipIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtils.getCompanyRelationshipIds( curriculumData );
    }

    /**
     * Returns the product IDs assigned to the provided curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @returns { string[] }
     */
    static getProductIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtils.getProductIds( curriculumData );
    }
    
    /**
     * Returns the locale IDs assigned to the provided curriculum.
     * 
     * @param { object } curriculumData - The curriculum data.
     * @returns { string[] }
     */
    static getLocaleIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtils.getLocaleIds( curriculumData );
    }

    /**
     * Filters a list of curricula to only those that are available to the provided brands. 
     * 
     * @param { object | object[] } curriculumData - The objects(s) to filter.
     * @param { string | string[] } brandIds - The brand IDs to use to filter content.
     * @returns { object[] }
     */
    static filterByBrands( curriculumData, brandIds ) {
        // Same logic as courses.
        return CourseUtils.filterByBrands( curriculumData, brandIds );
    }

    /**
     * Filters a list of curricula to only those that are available to the provided segments and brands. 
     * 
     * @param { object | object[] } curriculumData - The objects(s) to filter.
     * @param { string[] } brandIds - The brand IDs to use to filter content.
     * @param { string[] } userGroupIds - The group IDs to use to filter content.
     * @returns { object[] }
     */
    static filterByBrandsAndGroups( curriculumData, brandIds, userGroupIds ) {
        // Same logic as courses.
        return CourseUtils.filterByBrandsAndGroups( curriculumData, brandIds, userGroupIds );
    }

    /**
     * Returns only the categories that are connected to the provided curriculum data.
     * 
     * @param { object } curriculumData - The curriculum data to read.
     * @param { object[] } categoryDataList - The category list to look through for connections.
     * @returns { object[] }
     */
    static getConnectedCategories( curriculumData, categoryDataList ) {
        // Same logic as courses.
        return CourseUtils.getConnectedCategories( curriculumData, categoryDataList );
    }

}

/**
 * Returns whether or not the provided string is a JSON object in the form of a
 * string.
 * 
 * @param { string } str - The string to check.
 * @returns { boolean }
 */
function isValidJsonString( str ) {
    if ( !str ) { return false; }
    let parsedValue = undefined;
    try { parsedValue = JSON.parse( str ); }
    catch ( error ) { return false; }
    return ( typeof parsedValue === 'object' );
}
